import React, { useState } from 'react'
import {
  Alert,
  Chip,
  MenuItem,
  Select,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useAppRoleMembers, useUpdateAppRole } from '../hooks/useAppRoleMembers'
import { AppRoleError } from '../service/rbacAPI'
import { APP_ROLES, AppRole, AppRoleMember, AppRoleSource } from '../types/rbac.types'
import { isAppRole } from '../utils/capabilities'
import { APP_ROLE_ERROR_MESSAGES, ROLE_LABELS, rbacStrings } from '../i18n/rbacStrings'

type Feedback = { severity: 'success' | 'error'; message: string } | null

const errorMessage = (err: unknown): string =>
  err instanceof AppRoleError
    ? APP_ROLE_ERROR_MESSAGES[err.code]
    : APP_ROLE_ERROR_MESSAGES.unknown

const SOURCE_LABELS: Record<AppRoleSource, string> = {
  assigned: rbacStrings.sourceAssigned,
  default: rbacStrings.sourceDefault,
  role8000: rbacStrings.sourceRole8000,
}

// Table d'assignation des rôles du compte actif (admin uniquement — la garde
// de route est posée par la page). Une ligne est désactivée pendant son PUT,
// et en permanence quand son rôle vient du rôle 8000 (domainModel refuserait).
export default function AppRoleMembersTable() {
  const membersQuery = useAppRoleMembers()
  const updateMutation = useUpdateAppRole()
  const [savingUserId, setSavingUserId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback>(null)

  const handleChange = (member: AppRoleMember, next: AppRole) => {
    if (next === member.appRole) return
    setFeedback(null)
    setSavingUserId(member.userID)
    updateMutation.mutate(
      { userId: member.userID, appRole: next },
      {
        onSuccess: () => setFeedback({ severity: 'success', message: rbacStrings.saved }),
        onError: (err) => setFeedback({ severity: 'error', message: errorMessage(err) }),
        onSettled: () => setSavingUserId(null),
      },
    )
  }

  if (membersQuery.isLoading) {
    return <div style={{ fontSize: 13, color: '#6b7280' }}>{rbacStrings.loading}</div>
  }
  if (membersQuery.isError) {
    // Message ciblé selon le code backend (401, 403, 503…) ; repli générique.
    const err = membersQuery.error
    return (
      <Alert severity="error">
        {err instanceof AppRoleError && err.code !== 'unknown' && err.code !== 'forbidden'
          ? APP_ROLE_ERROR_MESSAGES[err.code]
          : err instanceof AppRoleError && err.code === 'forbidden'
            ? rbacStrings.listForbidden
            : rbacStrings.listError}
      </Alert>
    )
  }

  const members = membersQuery.data?.members ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} role="alert">
          {feedback.message}
        </Alert>
      )}

      {members.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>{rbacStrings.noMembers}</div>
      ) : (
        <TableContainer sx={{ background: '#fff', borderRadius: '20px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{rbacStrings.colName}</TableCell>
                <TableCell>{rbacStrings.colEmail}</TableCell>
                <TableCell>{rbacStrings.colRole}</TableCell>
                <TableCell>{rbacStrings.colSource}</TableCell>
                <TableCell>{rbacStrings.colStatus}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => {
                const isSaving = savingUserId === m.userID
                const managedBy8000 = m.source === 'role8000'
                return (
                  <TableRow key={m.userID}>
                    <TableCell>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {m.name || '—'}
                        {m.isAccountAdmin && (
                          <Chip size="small" color="primary" label={rbacStrings.accountAdmin} />
                        )}
                      </span>
                    </TableCell>
                    <TableCell>{m.email || '—'}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={isAppRole(m.appRole) ? m.appRole : ''}
                        displayEmpty
                        renderValue={(v) =>
                          isAppRole(v) ? (
                            ROLE_LABELS[v]
                          ) : (
                            <span style={{ color: '#6b7280' }}>{rbacStrings.noAccess}</span>
                          )
                        }
                        disabled={savingUserId !== null || managedBy8000}
                        onChange={(e) => {
                          const v = e.target.value
                          if (isAppRole(v)) handleChange(m, v)
                        }}
                        inputProps={{ 'aria-label': `${rbacStrings.colRole} ${m.email || m.name}` }}
                        sx={{ minWidth: 160 }}
                      >
                        {APP_ROLES.map((r) => (
                          <MenuItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </MenuItem>
                        ))}
                      </Select>
                      {isSaving && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>
                          {rbacStrings.saving}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={managedBy8000 ? rbacStrings.managedBy8000 : ''}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={SOURCE_LABELS[m.source] ?? rbacStrings.sourceDefault}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {m.suspended ? (
                        <Chip size="small" color="error" label={rbacStrings.suspended} />
                      ) : (
                        <Chip size="small" color="success" variant="outlined" label={rbacStrings.active} />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}
